// @oasis-permission-inherits: /session-replays (jerboa:view)
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import {
  loadSessionReplay,
  type LoadedSessionReplay,
} from "~/modules/session-replays/load-recording";
import { getSessionReplayQuery } from "~/modules/session-replays/queries";
import { SessionReplayPlayer } from "~/modules/session-replays/replay-player";

export const Route = createFileRoute("/_dash/session-replays/$replayId")({
  component: RouteComponent,
});

function RouteComponent() {
  const { replayId } = Route.useParams();
  const { data, isLoading, error } = getSessionReplayQuery.useQuery({
    pathParams: { replayId },
  });
  const [recording, setRecording] = useState<LoadedSessionReplay | null>(null);
  const [loadError, setLoadError] = useState<Error | null>(null);

  useEffect(() => {
    setRecording(null);
    setLoadError(null);
    if (!data) {
      return;
    }
    let active = true;
    void loadSessionReplay(data.result)
      .then((loaded) => {
        if (active) {
          setRecording(loaded);
        }
      })
      .catch((reason: unknown) => {
        if (active) {
          setLoadError(reason instanceof Error ? reason : new Error(String(reason)));
        }
      });
    return () => {
      active = false;
    };
  }, [data]);

  if (error || loadError) {
    return (
      <div className="px-6 py-4 text-sm text-destructive">
        Failed to load replay: {(error ?? loadError)?.message}
      </div>
    );
  }
  if (isLoading || !data || !recording) {
    return (
      <div className="space-y-3 px-6 py-4">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-[600px] w-full" />
      </div>
    );
  }

  const replay = data.result;
  return (
    <div className="space-y-4 px-6 py-4">
      <div>
        <h1 className="text-xl font-bold">Studio Session Replay</h1>
        <p className="font-mono text-xs text-muted-foreground">{replay.id}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Replay</CardTitle>
        </CardHeader>
        <CardContent>
          <SessionReplayPlayer detail={replay} recording={recording} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Capture details</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
            <Field label="Status" value={replay.status} />
            <Field label="Source" value={replay.sourcePath} />
            <Field label="Segments" value={String(replay.segmentCount)} />
            <Field label="Size" value={formatBytes(replay.totalBytes)} />
            <Field label="Viewport" value={`${replay.viewport.width}×${replay.viewport.height}`} />
            <Field label="Created" value={new Date(replay.createdAt).toLocaleString()} />
            <Field label="Last chunk" value={new Date(replay.lastCheckpointAt).toLocaleString()} />
            <Field label="Expires" value={new Date(replay.expiresAt).toLocaleString()} />
          </dl>
          {replay.canvasIssues.length > 0 && (
            <div className="mt-4 rounded-md border border-destructive/40 p-3 text-xs text-destructive">
              {replay.canvasIssues.join(" · ")}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="break-all font-mono text-xs">{value}</dd>
    </div>
  );
}

function formatBytes(bytes: number) {
  if (bytes < 1_000_000) {
    return `${(bytes / 1_000).toFixed(1)} KB`;
  }
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}
